import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked, NgZone,  LOCALE_ID, Inject } from '@angular/core';
import { FormControl, FormGroupDirective, NgForm, Validators, FormGroup, FormBuilder, AbstractControl } from '@angular/forms';
import { MatDialog } from '@angular/material';
import { HttpClient } from '@angular/common/http';

import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog';

import { environment } from '../environments/environment';
import { Observable } from 'rxjs/Observable';
import { startWith } from 'rxjs/operators/startWith';
import { map } from 'rxjs/operators/map';
import { Country, ContactRequest, CompanySector, ContactMethod, RawCountry, SendContactResponse } from './models';
import { EmailValidator, IsObject } from './utils';

import { ActivatedRoute } from '@angular/router';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit, AfterViewChecked {

    @ViewChild('contentContainer') contentContainerRef: ElementRef;

    public isProduction = environment.production;
    public filteredCountries: Observable<Country[]>;

    public maxDate = new Date();

    public mainForm: FormGroup;
    public products: string[];
    public address: string = null;
    public resellerLocation: string = null;
    public isLoading = false;
    public countries: Country[];
    public lang: string;

    public usaTema: string = "";
    private lastSentHeight: number = 0; // Memorizza l'ultima altezza inviata

    CountriesUrl = 'https://restcountries.com/v2/';


    constructor(
        private fb: FormBuilder,
        private http: HttpClient,
        public dialog: MatDialog,
        @Inject(LOCALE_ID) locale: string,
        private route: ActivatedRoute,
        private elementRef: ElementRef,
        private ngZone: NgZone) {

        this.lang = locale;

        this.createForm();

    }

    ngOnInit() {

        this.http.get<RawCountry[]>(this.CountriesUrl + 'all?fields=nativeName,alpha3Code,alpha2Code').subscribe((data) => {

            this.countries = [];

            for (let n = 0; n < data.length; n++) {
                const country = data[n];

                this.countries.push({
                    name: country.nativeName,
                    code: country.alpha3Code,
                    shortCode: country.alpha2Code
                });
            }

            this.filteredCountries = this.mainForm.get('country').valueChanges
                .pipe(startWith<string | Country>(''),
                    map(value => typeof value === 'string' ? value : value.name),
                    map(name => name ? this.filter(name) : this.countries.slice()));

        });

        this.route.queryParams.subscribe(params =>  {
            this.usaTema = params['theme'] || "";
        });
    }

    
    ngAfterViewChecked() {
        if (this.contentContainerRef) {
            const currentHeight = this.contentContainerRef.nativeElement.offsetHeight;
        
            // Controllo per inviare il messaggio solo se l'altezza è cambiata
            if (currentHeight > 0 && currentHeight !== this.lastSentHeight) {
                // Usa NgZone.run() per uscire dalla change detection e inviare
                // Il postMessage non deve innescare un nuovo ciclo di CD.
                this.ngZone.runOutsideAngular(() => {
                    window.parent.postMessage({ 'frameHeight': (currentHeight/* + 120/*aggiungo il padding:60 */) }, '*');
                });
                this.lastSentHeight = currentHeight;
            }
        }
    }
    

    filter(name: string): Country[] {
        return this.countries.filter(option =>
            option.name.toLowerCase().indexOf(name.toLowerCase()) === 0);
    }

    displayFn(user?: Country): string | undefined {
        return user ? user.name : undefined;
    }

    createForm() {

        this.mainForm = this.fb.group({
            email: ['', [
                Validators.required,
                EmailValidator
            ]],
            phone: ['', [
            ]],
            company: ['', [
                Validators.required
            ]],
            contactMethod: ['0', [
                Validators.required
            ]],
            surname: ['', [
                Validators.required,
            ]],
            job: ['', [
                Validators.required,
            ]],
            firstName: ['', [
                Validators.required,
            ]],
            country: ['', [
                Validators.required, IsObject
            ]],
            note: ['', [

            ]],
            sector: [0, [
                Validators.required
            ]],
            canContact: ['', [
                Validators.requiredTrue
            ]]
        });

        this.mainForm.get('contactMethod').valueChanges.subscribe(val => {

            // console.log('ValueChanged: ' + val);

            if (val === '0') {
                // console.log('Switching to mail');
                this.mainForm.get('email').setValidators([Validators.required, EmailValidator]);
                this.mainForm.get('email').updateValueAndValidity();
                this.mainForm.get('phone').clearValidators();
                this.mainForm.get('phone').updateValueAndValidity();
            } else if (val === '1') {
                // console.log('Switching to phone');
                this.mainForm.get('phone').setValidators([Validators.required]);
                this.mainForm.get('phone').updateValueAndValidity();
                this.mainForm.get('email').clearValidators();
                this.mainForm.get('email').updateValueAndValidity();
            }
        });
    }

    onSubmit(form: any) {

        // const challenge = this.recaptchaTokenService.getRecaptchaToken();

        const requestObj: ContactRequest = {
            firstName: this.mainForm.get('firstName').value,
            surname: this.mainForm.get('surname').value,
            company: this.mainForm.get('company').value,
            sector: this.mainForm.get('sector').value,
            job: this.mainForm.get('job').value,
            country: this.mainForm.get('country').value.shortCode,
            contactMethod: this.mainForm.get('contactMethod').value,
            mail: this.mainForm.get('email').value,
            phone: this.mainForm.get('phone').value,
            // captchaChallenge: null, // challenge,
            note: this.mainForm.get('note').value,
            language: this.lang
        };

        this.isLoading = true;

        this.http.post<SendContactResponse>(environment.sendContactRequestUrl,
            requestObj)
            .subscribe((data) => {

                this.isLoading = false;

                if (data !== SendContactResponse.Ok) {
                    this.dialog.open(ConfirmDialogComponent, {
                        width: '250px',
                        data: { messageId: 1 }
                    });

                    return;
                }

                this.dialog.open(ConfirmDialogComponent, {
                    width: '250px',
                    data: { messageId: 2 }
                }).afterClosed().subscribe(() => location.reload());

            }, err => {
                this.isLoading = false;
                this.dialog.open(ConfirmDialogComponent, {
                    width: '250px',
                    data: { messageId: 0 }
                });
            });
    }

    testData() {
        this.mainForm.patchValue({
            firstName: 'FirstName',
            surname: 'Surname',
            job: 'Job',
            country: 'Italia',
            phone: '023478234',
            email: 'tech@digipa.it',
            company: 'Company',
            canContact: true,
            sector: 0,
            contactMethod: 0,
            note: 'Note Note Note Note Note'
        });
    }


    onScriptLoad() {
        console.log('Google reCAPTCHA loaded and is ready for use!');
    }

    onScriptError() {
        console.log('Something went long when loading the Google reCAPTCHA');
    }
}


// WEBPACK FOOTER //
// ./src/app/app.component.ts